// Pure pieces of "Courtney rejoins the same chat after a drop": the transcript
// we hand the new agent, and the retry loop that asks the widget server for one.
// The React wiring lives in useCallMachine.

export type TranscriptTurn = { role: "user" | "assistant"; text: string };

// Must stay in sync with web/src/rejoin.ts (server-side caps).
export const MAX_TRANSCRIPT_TURNS = 20;
export const MAX_TURN_CHARS = 4000;
// Server cap is 16 KB; leave headroom for JSON overhead differences.
export const MAX_TRANSCRIPT_BYTES = 15 * 1024;

// Backoff before each dispatch attempt, so 3 attempts per drop.
export const REJOIN_DELAYS_MS = [1_000, 3_000, 8_000];
// How long to wait for a dispatched agent to show up before trying again.
export const REJOIN_JOIN_WAIT_MS = 15_000;
// Runaway guard: a session that keeps losing its agent stops rejoining.
export const MAX_REJOINS_PER_SESSION = 5;

// The SDK's wording when the agent participant disconnects mid-conversation.
export const AGENT_LEFT_REASON = "Agent left the room unexpectedly.";

type MessageLike = {
  type?: string;
  message: string;
  from?: { identity?: string } | null;
};

const byteLength = (s: string) => new TextEncoder().encode(s).length;

/** Last turns of the on-screen thread as plain role+text, capped in count and
 *  bytes (oldest dropped first) so the server accepts it. */
/**
 * RPC the agent calls on this participant when LiveKit re-delivers its job after
 * a worker crash: the new agent arrives with no history, so it asks for what is
 * on screen. Same shape as the /api/rejoin body. Mirrors TRANSCRIPT_RPC_METHOD
 * in src/resume.py.
 */
export const TRANSCRIPT_RPC_METHOD = "getTranscript";

export function buildTranscript(
  messages: readonly MessageLike[],
  localIdentity: string,
): TranscriptTurn[] {
  const turns: TranscriptTurn[] = [];
  for (const m of messages) {
    const text = (m.message ?? "").trim();
    if (!text) continue;
    const isUser =
      m.type === "userTranscript" ||
      (m.type === "chatMessage" && m.from?.identity === localIdentity);
    turns.push({ role: isUser ? "user" : "assistant", text: text.slice(0, MAX_TURN_CHARS) });
  }
  const recent = turns.slice(-MAX_TRANSCRIPT_TURNS);
  while (recent.length > 0 && byteLength(JSON.stringify(recent)) > MAX_TRANSCRIPT_BYTES) {
    recent.shift();
  }
  return recent;
}

/** True when the only thing wrong is that the agent left (not a connect timeout). */
export function isAgentLeftOnly(failureReasons: readonly string[] | null | undefined): boolean {
  return (
    !!failureReasons &&
    failureReasons.length > 0 &&
    failureReasons.every((r) => r === AGENT_LEFT_REASON)
  );
}

/**
 * A running rejoin loop stops when the chat is over or the agent's absence is
 * intentional: the user left or reset it ("New chat" sets userEnded), it
 * ended, the session was torn down, the hook unmounted, the room is gone, or
 * the agent left a quiet chat for idle. None of these is answered with a new
 * agent.
 */
export function isRejoinCancelled(
  live: {
    unmounted: boolean;
    userEnded: boolean;
    conversationEnded: boolean;
    sessionStarted: boolean;
    // The agent left on purpose after a long idle; its departure is not a drop.
    idleEnded?: boolean;
  },
  roomDisconnected: boolean,
): boolean {
  return (
    live.unmounted ||
    live.userEnded ||
    !!live.idleEnded ||
    live.conversationEnded ||
    !live.sessionStarted ||
    roomDisconnected
  );
}

export type RejoinOutcome = "joined" | "failed" | "cancelled";

export async function runRejoin({
  dispatch,
  agentPresent,
  cancelled,
  sleep = (ms) => new Promise((r) => setTimeout(r, ms)),
  delaysMs = REJOIN_DELAYS_MS,
  joinWaitMs = REJOIN_JOIN_WAIT_MS,
  pollMs = 250,
  onAttempt,
}: {
  /** Ask the server for a new agent. Resolves when the request was accepted. */
  dispatch: () => Promise<void>;
  agentPresent: () => boolean;
  cancelled: () => boolean;
  sleep?: (ms: number) => Promise<void>;
  delaysMs?: number[];
  joinWaitMs?: number;
  pollMs?: number;
  onAttempt?: (attempt: number) => void;
}): Promise<RejoinOutcome> {
  for (let attempt = 0; attempt < delaysMs.length; attempt++) {
    await sleep(delaysMs[attempt]);
    if (cancelled()) return "cancelled";
    // Never create a second agent: if one has appeared on its own, we're done.
    if (agentPresent()) return "joined";
    onAttempt?.(attempt + 1);
    try {
      await dispatch();
    } catch (err) {
      console.warn(`[lk-widget] rejoin attempt ${attempt + 1} failed:`, err);
      continue;
    }
    for (let waited = 0; waited < joinWaitMs; waited += pollMs) {
      if (cancelled()) return "cancelled";
      if (agentPresent()) return "joined";
      await sleep(pollMs);
    }
    if (cancelled()) return "cancelled";
    if (agentPresent()) return "joined";
  }
  return "failed";
}
